import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../components/AuthContext";
import { Bell, Users, BookOpen, Calendar, MessageSquare, FileText, LogOut, ChevronDown, ChevronUp, Check, X, Zap, Settings, LayoutDashboard, Smartphone } from "lucide-react";
import HebrewCalendarWidget from "../components/HebrewCalendarWidget";

import API from "@/apiConfig";

const ROLE_BADGE = {
  it_admin: { label: "IT Admin", cls: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30" },
  admin:       { label: "Admin",       cls: "bg-purple-500/20 text-purple-300 border border-purple-500/30" },
  staff:       { label: "Staff",       cls: "bg-blue-500/20  text-blue-300  border border-blue-500/30" },
  member:      { label: "Member",      cls: "bg-gray-500/20  text-gray-300  border border-gray-500/30" },
};

export default function DashPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isSuperAdmin, isHost, isApostle, hostedCollectives, logout, refresh } = useAuth();
  const role = user?.role || "member";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [collectives, setCollectives] = useState([]);
  const [pendingByCollective, setPendingByCollective] = useState({});
  const [expandedCollective, setExpandedCollective] = useState(null);
  const [events, setEvents] = useState([]);
  const [showEventPush, setShowEventPush] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedCollectives, setSelectedCollectives] = useState([]);
  const [pushStatus, setPushStatus] = useState("");

  const fetchNotifs = useCallback(async () => {
    try {
      const [n, u] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/unread-count`),
      ]);
      setNotifications(n.data || []);
      setUnreadCount(u.data?.count || 0);
    } catch {}
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const ids = isAdmin || isSuperAdmin
        ? (await axios.get(`${API}/collectives`)).data.map(c => c.id)
        : hostedCollectives.map(c => c.id);

      const results = {};
      await Promise.all(ids.map(async (id) => {
        const res = await axios.get(`${API}/host/collectives/${id}/requests`);
        const pending = (res.data || []).filter(r => r.status === "pending");
        if (pending.length > 0) results[id] = pending;
      }));
      setPendingByCollective(results);
    } catch {}
  }, [isAdmin, isSuperAdmin, hostedCollectives]);

  useEffect(() => {
    if (!user) return;
    fetchNotifs();
    if (isAdmin || isSuperAdmin || isHost) fetchPending();
    if (isSuperAdmin || isAdmin) {
      axios.get(`${API}/collectives`).then(r => setCollectives(r.data || []));
      axios.get(`${API}/events`).then(r => setEvents(r.data || []));
    }
  }, [user, isAdmin, isSuperAdmin, isHost, fetchNotifs, fetchPending]);

  const markAllRead = async () => {
    await axios.post(`${API}/notifications/read-all`);
    fetchNotifs();
  };

  const handleApprove = async (collectiveId, reqId) => {
    await axios.post(`${API}/host/collectives/${collectiveId}/requests/${reqId}/approve`, { welcome_note: "" });
    fetchPending();
    fetchNotifs();
  };

  const handleDeny = async (collectiveId, reqId) => {
    await axios.post(`${API}/host/collectives/${collectiveId}/requests/${reqId}/reject`, {});
    fetchPending();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handlePushEvent = async () => {
    if (!selectedEvent) return;
    setPushStatus("Pushing…");
    try {
      const res = await axios.post(`${API}/super-admin/push-event`, {
        event_id: selectedEvent,
        collective_ids: selectedCollectives,
      });
      setPushStatus(`Pushed to ${res.data.pushed_to} collective(s), ${res.data.notifications_sent} notified.`);
      setSelectedEvent("");
      setSelectedCollectives([]);
    } catch {
      setPushStatus("Failed. Please try again.");
    }
  };

  const totalPending = Object.values(pendingByCollective).reduce((s, arr) => s + arr.length, 0);
  const badge = ROLE_BADGE[role] || ROLE_BADGE.member;

  // Chunk 1.1 — Role gate: only collective_host, it_admin, admin, apostle
  const canAccessDash = isAdmin || isHost;

  useEffect(() => {
    if (user && !canAccessDash) {
      toast("This area is for Collective leaders", { duration: 4000 });
      navigate("/collectives");
    }
  }, [user, canAccessDash, navigate]);

  if (!user || !canAccessDash) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-navy bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0f2545] via-navy to-[#050b14] pb-24 px-4 sm:px-6 pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white tracking-tight" data-testid="dash-heading">
                Guide Dashboard
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                <span className="text-sm text-white/80 font-medium">{user.display_name || user.name}</span>
              </div>
            </div>

            {/* Bell */}
            <button
              data-testid="notifications-bell"
              onClick={() => setShowNotifs(!showNotifs)}
              className="relative p-2.5 rounded-xl bg-white/5 border border-white/10 text-gold hover:bg-white/10 hover:scale-105 transition-all duration-300"
            >
              <Bell size={24} />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-sm">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Hebrew Calendar Widget */}
          <div className="mb-8 relative z-10 drop-shadow-xl">
            <HebrewCalendarWidget />
          </div>

          {/* Notification Drawer */}
          {showNotifs && (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 mb-8 shadow-xl shadow-black/20" data-testid="notif-drawer">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <span className="text-base font-heading font-semibold text-white tracking-wide">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-sm text-gold hover:text-yellow-400 transition-colors">Mark all read</button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="text-center py-6 text-white/50 text-sm">
                  <Bell className="mx-auto h-8 w-8 mb-2 opacity-50" />
                  <p>No new notifications</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {notifications.map(n => (
                    <div key={n.id} className={`p-4 rounded-xl text-sm transition-all duration-300 ${n.read ? "bg-white/5 text-white/70" : "bg-gold/10 border border-gold/30 text-white shadow-sm"}`}>
                      <p className="font-semibold">{n.title}</p>
                      <p className="text-xs mt-1.5 opacity-80 leading-relaxed">{n.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pending Join Requests */}
          {(isAdmin || isSuperAdmin || isHost) && totalPending > 0 && (
            <div className="bg-white/5 backdrop-blur-md border border-gold/30 rounded-2xl p-5 mb-8 shadow-lg shadow-black/20" data-testid="pending-requests">
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                <div className="p-2 rounded-lg bg-gold/10 text-gold">
                  <Users size={18} />
                </div>
                <span className="text-base font-heading font-semibold text-white tracking-wide">
                  Pending Requests ({totalPending})
                </span>
              </div>
              {Object.entries(pendingByCollective).map(([cid, reqs]) => {
                const collective = collectives.find(c => c.id === cid);
                const name = collective?.name || cid;
                const isExpanded = expandedCollective === cid;
                return (
                  <div key={cid} className="mb-4 last:mb-0">
                    <button
                      onClick={() => setExpandedCollective(isExpanded ? null : cid)}
                      className="w-full flex items-center justify-between text-sm font-medium text-white/90 bg-white/5 hover:bg-white/10 rounded-xl px-4 py-3 transition-colors"
                    >
                      <span>{name} <span className="text-gold ml-1.5 font-bold">({reqs.length})</span></span>
                      {isExpanded ? <ChevronUp size={16} className="text-white/50" /> : <ChevronDown size={16} className="text-white/50" />}
                    </button>
                    {isExpanded && (
                      <div className="mt-2 space-y-2 pl-2">
                        {reqs.map(r => (
                          <div key={r.id} className="bg-black/20 border border-white/5 rounded-xl p-4 flex items-start gap-4">
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-white">{r.display_name || r.name}</p>
                              {r.bio && <p className="text-xs text-white/70 mt-1 leading-relaxed">{r.bio}</p>}
                              {r.message && <p className="text-xs text-gold/80 mt-1.5 italic border-l-2 border-gold/30 pl-2">"{r.message}"</p>}
                            </div>
                            <div className="flex gap-2">
                              <button
                                data-testid={`approve-${r.id}`}
                                onClick={() => handleApprove(cid, r.id)}
                                className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                              ><Check size={16} /></button>
                              <button
                                data-testid={`deny-${r.id}`}
                                onClick={() => handleDeny(cid, r.id)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              ><X size={16} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Event Push — IT Admin / Admin only */}
          {(isSuperAdmin || isAdmin) && (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 mb-8 shadow-lg shadow-black/20">
              <button
                data-testid="event-push-toggle"
                onClick={() => setShowEventPush(!showEventPush)}
                className="w-full flex items-center justify-between text-base font-heading font-semibold text-white tracking-wide pb-3 border-b border-white/10 mb-2"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                    <Zap size={18} />
                  </div>
                  Push Event to Collectives
                </div>
                {showEventPush ? <ChevronUp size={16} className="text-white/50" /> : <ChevronDown size={16} className="text-white/50" />}
              </button>
              {showEventPush && (
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="text-xs text-white/60 uppercase tracking-widest mb-1.5 block font-medium">Select Event</label>
                    <select
                      data-testid="push-event-select"
                      value={selectedEvent}
                      onChange={e => setSelectedEvent(e.target.value)}
                      className="w-full bg-black/30 text-white border border-white/20 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-gold/50 focus:border-gold/50 outline-none transition-all"
                    >
                      <option value="">— Choose an event —</option>
                      {events.map(e => (
                        <option key={e.id} value={e.id}>{e.title} ({e.date})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 uppercase tracking-widest mb-2 block font-medium">Target Collectives (none = all)</label>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                      {collectives.map(c => (
                        <label key={c.id} className="flex items-center gap-3 text-sm text-white/80 cursor-pointer bg-white/5 p-2.5 rounded-lg hover:bg-white/10 transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedCollectives.includes(c.id)}
                            onChange={e => {
                              if (e.target.checked) setSelectedCollectives(prev => [...prev, c.id]);
                              else setSelectedCollectives(prev => prev.filter(id => id !== c.id));
                            }}
                            className="w-4 h-4 rounded border-white/30 text-gold focus:ring-gold bg-black/50"
                          />
                          {c.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <button
                    data-testid="push-event-btn"
                    onClick={handlePushEvent}
                    disabled={!selectedEvent}
                    className="w-full bg-gradient-to-r from-gold to-yellow-600 hover:from-yellow-500 hover:to-gold disabled:opacity-40 disabled:cursor-not-allowed text-navy font-bold py-3.5 rounded-xl text-sm transition-all shadow-md shadow-gold/20"
                  >
                    Push Event
                  </button>
                  {pushStatus && <p className="text-xs text-center text-white/80 font-medium bg-black/20 py-2 rounded-lg">{pushStatus}</p>}
                </div>
              )}
            </div>
          )}

          {/* Quick Shortcuts Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
            {(isAdmin || isSuperAdmin) && (
              <>
                <ShortcutCard icon={<Users size={22} />} label="All Collectives" onClick={() => navigate("/wa-ops-center?tab=collectives")} />
                <ShortcutCard icon={<MessageSquare size={22} />} label="Social Feed" onClick={() => navigate("/wa-ops-center?tab=social")} />
                <ShortcutCard icon={<Calendar size={22} />} label="Events" onClick={() => navigate("/wa-ops-center?tab=events")} />
                <ShortcutCard icon={<FileText size={22} />} label="Page Requests" onClick={() => navigate("/wa-ops-center?tab=rentals")} />
                <ShortcutCard icon={<Smartphone size={22} />} label="SMS Broadcast" onClick={() => navigate("/wa-ops-center?tab=events")} />
              </>
            )}
            {(isHost && !isAdmin && !isSuperAdmin) && hostedCollectives.map(c => (
              <ShortcutCard
                key={c.id}
                icon={<Users size={22} />}
                label={`Manage ${c.name}`}
                onClick={() => navigate(`/host/${c.slug || c.id}`)}
              />
            ))}
          </div>

          {/* Logout */}
          <button
            data-testid="dash-logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 border-2 border-red-500/20 text-red-400 hover:bg-red-500/10 py-3.5 rounded-xl text-sm font-semibold transition-all duration-300"
          >
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </div>
    </Layout>
  );
}

function ShortcutCard({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/30 rounded-2xl p-5 flex flex-col items-center gap-3 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg shadow-black/20 group"
    >
      <span className="text-white/70 group-hover:text-gold transition-colors">{icon}</span>
      <span className="text-sm font-medium text-white/90 group-hover:text-white transition-colors">{label}</span>
    </button>
  );
}
